import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { allowedBirthYears } from "@/lib/tarbawiya/age-band";

export type TarbawiyaChild = {
  id: string;
  first_name: string;
  birth_year: number;
  sex: "male" | "female" | null;
};

async function assertCanView() {
  const { tarbawiyaCanView } = await import("@/lib/tarbawiya/gate.server");
  if (!(await tarbawiyaCanView())) throw new Error("Not available");
}

async function myProfileId(supabase: any, userId: string): Promise<string> {
  const { data } = await supabase.from("profiles").select("id").eq("user_id", userId).maybeSingle();
  if (!data?.id) throw new Error("No profile");
  return data.id as string;
}

export const getTarbawiyaGate = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async () => {
    const { tarbawiyaCanView } = await import("@/lib/tarbawiya/gate.server");
    return { canView: await tarbawiyaCanView() };
  });

/** The caller's own children (RLS scopes the read to the owner). */
export const listMyChildren = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<TarbawiyaChild[]> => {
    await assertCanView();
    const { supabase, userId } = context as { supabase: any; userId: string };
    const clientId = await myProfileId(supabase, userId);
    const { data, error } = await supabase
      .from("children")
      .select("id, first_name, birth_year, sex")
      .eq("client_id", clientId)
      .order("created_at", { ascending: true });
    if (error) throw error;
    return (data ?? []) as TarbawiyaChild[];
  });

const addChildSchema = z.object({
  first_name: z.string().trim().min(1).max(40),
  birth_year: z.number().int().refine((y) => allowedBirthYears().includes(y), "out_of_range"),
  sex: z.enum(["male", "female"]).nullable(),
});

export const addChild = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => addChildSchema.parse(d))
  .handler(async ({ data, context }): Promise<TarbawiyaChild> => {
    await assertCanView();
    const { supabase, userId } = context as { supabase: any; userId: string };
    const clientId = await myProfileId(supabase, userId);
    const { data: row, error } = await supabase
      .from("children")
      .insert({ client_id: clientId, ...data })
      .select("id, first_name, birth_year, sex")
      .single();
    if (error) throw error;
    return row as TarbawiyaChild;
  });
